/* eslint-disable @angular-eslint/prefer-signals */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { getColorStates } from '@plugins/client/colors/color-utils';
import { focusElement } from '@plugins/client/utils';

@Component({
  selector: 'plugins-button',
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ButtonComponent implements OnChanges {
  @ViewChild('button', { static: true }) button!: ElementRef<HTMLButtonElement>;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() primary = true;
  @Input() secondary = false;
  @Input() text = false;
  @Input() disabled = false;
  @Input() buttonClass?: string;
  @Input() buttonId?: string;
  @Input() buttonStyle?: Record<string, string | number>;
  @Input() ariaRole = 'button';
  @Input() ariaHasPopup?: boolean | string;
  @Input() ariaExpanded?: boolean;
  @Input() ariaControls?: string;
  @Input() ariaOwns?: string;
  @Input() ariaActiveDescendant?: string;
  @Input() ariaSelected?: boolean;
  @Input() ariaLabel?: string;
  @Input() ariaDescription?: string;
  @Input() set color(val: string) {
    this.setColor(val);
  }
  @Input() size?: 'lg' | 'sm' | 'xs';
  @Input() ariaPressed?: boolean;
  @Input() ariaInvalid?: boolean;
  @Output() readonly clicked = new EventEmitter<MouseEvent>();
  @Output() readonly mouseEntered = new EventEmitter<Event>();
  @Output() readonly mouseLeft = new EventEmitter<Event>();
  @Output() readonly focused = new EventEmitter<Event>();
  @Output() readonly blurred = new EventEmitter<Event>();
  btnFocused = false;
  baseColor!: string;
  backgroundColor!: string;
  boxShadow!: string;
  disabledColor = '#545454';

  constructor() {
    this.setColor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.disabled) {
      this.btnFocused = false;
    }
    if (changes['disabled'] || changes['secondary']) {
      this.updateBackgroundColor();
    }
  }

  private setColor(color = 'black') {
    const { baseColor, focusColor } = getColorStates(color);
    this.boxShadow = `0 0 0 0.2rem ${focusColor}`;
    this.baseColor = baseColor;
    this.updateBackgroundColor();
  }

  private updateBackgroundColor() {
    this.backgroundColor = this.secondary
      ? this.disabled
        ? this.disabledColor
        : this.baseColor
      : 'inherit';
  }

  public handleFocus(evt: Event) {
    this.btnFocused = true;
    this.focused.emit(evt);
  }

  public handleBlur(evt: Event) {
    this.btnFocused = false;
    this.blurred.emit(evt);
  }

  public generateStyle(style: Record<string, string | number>) {
    return Object.assign(style, this.buttonStyle);
  }

  public focus() {
    focusElement(this.button.nativeElement);
  }
}
